import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from '@/components/icons';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { PrntrCard } from './ui/prntr-input';
import { TextRow } from './ui/prntr-fields';
import { FormSkeleton } from './ui/skeleton-presets';
import { useTripCreate, useTripGetById, useTripUpdate } from '@/lib/hooks';
import { track, EVENTS } from '@/utils/analytics';


// A trip is just a logical grouping of flights — aircraft is picked per flight.
const tripFormSchema = z.object({
  tripName: z.string().min(1, 'Trip name is required'),
})

type TripFormData = z.infer<typeof tripFormSchema>;

interface TripDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tripId?: string; // If provided, drawer opens in edit mode
  onSuccess?: (id: string) => void; // Called after successful create/update
}

export function TripDrawer({ isOpen, onClose, tripId, onSuccess }: TripDrawerProps) {
  const isEditMode = !!tripId;

  // Fetch trip data if in edit mode
  const { data: tripData, isLoading: isLoadingTrip } = useTripGetById(tripId)

  // Mutations
  const createMutation = useTripCreate();
  const updateMutation = useTripUpdate(tripId);

  const defaultValues: TripFormData = {
    tripName: ''
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<TripFormData>({
    resolver: zodResolver(tripFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  // Populate form when drawer opens with trip data
  useEffect(() => {
    if (isOpen && isEditMode && tripData) {
      reset({
        tripName: tripData.tripName,
      });
    }
  }, [isOpen, isEditMode, tripData, reset]);

  const watchedTripName = watch('tripName');

  const isLoadingEditData = isEditMode && isLoadingTrip

  const resetForm = () => {
    reset(defaultValues);
  };

  const onSubmit = async (_data: TripFormData) => {
    try {
      if (isEditMode && tripId) {
        // Update existing trip
        const trip = await updateMutation.mutateAsync({
          id: tripId,
          tripName: watchedTripName,
        });
        resetForm();
        onClose();
        onSuccess(trip.id)
      } else {
        // Create new trip
        const trip = await createMutation.mutateAsync({
          tripName: watchedTripName,
        });
        track(EVENTS.TRIP_CREATED, { tripId: trip.id })
        onSuccess?.(trip.id);

      }

      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to save trip:', error);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              {isEditMode ? 'Edit Trip' : 'Create Trip'}
            </DrawerTitle>
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleCancel}>
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Scrollable Content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {isLoadingEditData ? (
              <FormSkeleton />
            ) : (
            <form id="trip-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <PrntrCard>
                <TextRow nullable={false}
                  control={control}
                  name="tripName"
                  label="Trip Name *"
                  placeholder="Add"
                  error={errors.tripName?.message}
                />
              </PrntrCard>
            </form>
            )}
          </div>

          {/* Footer */}
          <DrawerFooter>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="mini"
                onClick={handleCancel}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="mini"
                form="trip-form"
                disabled={!isValid || !isDirty || isSubmitting || isLoadingEditData}
                className="flex-1"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
